import { useEffect, useId, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { programsApi, type ProgramDeletionImpactItem, type ThemeStatus } from '@/api/programs'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

/** Что удаляется из шаблона: тема или раздел */
export type ProgramNodeDeletionTarget = {
  kind: 'theme' | 'section'
  id: number
  title: string
}

const STATUS_KEY: Record<ThemeStatus, 'planned' | 'inProgress' | 'done'> = {
  planned: 'planned',
  in_progress: 'inProgress',
  done: 'done',
}

const STATUS_COLOR: Record<ThemeStatus, 'default' | 'warning' | 'success'> = {
  planned: 'default',
  in_progress: 'warning',
  done: 'success',
}

/**
 * Подтверждение удаления темы или раздела шаблона.
 *
 * Синхронизация структуры сама из копий учеников ничего не убирает: решает
 * учитель. Окно открывается сразу и спрашивает у сервера, у кого есть копия
 * (назначения с автообновлением, не завершённые, с активным договором). Пустой
 * список — обычное подтверждение: удаляется только шаблон. Иначе — список с
 * галочками (все отмечены), и DELETE уходит с отмеченными назначениями; у
 * снятых копия остаётся.
 *
 * Удалить, не дождавшись списка, нельзя: запрос без него молча оставил бы
 * копии у всех — ровно то, от чего окно и защищает.
 */
export function ProgramNodeDeletionDialog({
  programId,
  target,
  onClose,
  onDeleted,
}: {
  programId: number
  target: ProgramNodeDeletionTarget | null
  onClose: () => void
  onDeleted: () => void
}) {
  const { t } = useTranslation()
  const listId = useId()

  const [selected, setSelected] = useState<number[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Ответ предпросмотра вместе с ключом запроса, на который он пришёл
  const [loaded, setLoaded] = useState<{
    key: string
    items: ProgramDeletionImpactItem[]
    error: string | null
  } | null>(null)

  // Последняя цель держится и после закрытия: иначе на анимации закрытия
  // заголовок и текст окна мигали пустыми. Каждое открытие — новый сеанс:
  // повторно открытая та же тема перечитывает список, а не показывает старый
  const [shown, setShown] = useState<ProgramNodeDeletionTarget | null>(null)
  const [session, setSession] = useState(0)
  if (target !== null && target !== shown) {
    setShown(target)
    setSession((value) => value + 1)
    setSubmitError(null)
  }

  const requestKey = target === null ? null : `${session}:${reloadKey}`
  // Загрузка выводится, а не ставится в эффекте: эффект срабатывает после
  // отрисовки, и первый кадр окна показывал бы пустое подтверждение с живой
  // кнопкой «Удалить»
  const loading = requestKey !== null && loaded?.key !== requestKey
  const items = loaded?.items ?? []
  const loadError = loaded?.error ?? null

  useEffect(() => {
    if (requestKey === null || shown === null) return

    let cancelled = false
    const request =
      shown.kind === 'theme'
        ? programsApi.themeDeletionImpact(programId, shown.id)
        : programsApi.sectionDeletionImpact(programId, shown.id)

    request
      .then((list) => {
        if (cancelled) return
        setLoaded({ key: requestKey, items: list, error: null })
        // По умолчанию убираем у всех: учитель снимает галочки у исключений
        setSelected(list.map((item) => item.assignment_id))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoaded({
          key: requestKey,
          items: [],
          error: displayErrorMessage(getErrorMessage(err), t('programs.deletion.loadError')),
        })
        setSelected([])
      })

    return () => {
      cancelled = true
    }
  }, [requestKey, shown, programId, t])

  const close = () => {
    if (submitting) return
    onClose()
  }

  const toggle = (assignmentId: number) =>
    setSelected((prev) =>
      prev.includes(assignmentId) ? prev.filter((id) => id !== assignmentId) : [...prev, assignmentId],
    )

  const allSelected = items.length > 0 && selected.length === items.length
  const someSelected = selected.length > 0 && !allSelected

  const startedSelected =
    shown?.kind === 'theme'
      ? items.filter(
          (item) =>
            selected.includes(item.assignment_id) &&
            item.theme_status !== null &&
            item.theme_status !== 'planned',
        ).length
      : 0

  const submit = async () => {
    if (target === null) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      // Порядок отмеченных — как в списке: так проще читать запрос в журнале
      const ids = items.length === 0
        ? undefined
        : items.flatMap((item) => (selected.includes(item.assignment_id) ? [item.assignment_id] : []))

      if (target.kind === 'theme') await programsApi.deleteTheme(programId, target.id, ids)
      else await programsApi.deleteSection(programId, target.id, ids)

      onDeleted()
    } catch (err) {
      setSubmitError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setSubmitting(false)
    }
  }

  const isTheme = shown?.kind === 'theme'
  const title = shown?.title ?? ''

  const recipientLabel = (item: ProgramDeletionImpactItem): string => {
    const who =
      item.recipient.type === 'group' ? t('programs.deletion.groupRecipient') : t('programs.student')

    return item.assignment_status === 'paused' ? `${who} · ${t('programs.status.paused')}` : who
  }

  return (
    <Dialog open={target !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{isTheme ? t('programs.deleteTheme') : t('programs.deleteSection')}</DialogTitle>
      <DialogContent>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" width="70%" height={24} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={48} />
          </Box>
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setReloadKey((key) => key + 1)}>
                {t('common.retry')}
              </Button>
            }
          >
            {loadError}
          </Alert>
        ) : items.length === 0 ? (
          <DialogContentText>
            {isTheme
              ? t('programs.deleteThemeConfirm', { title })
              : t('programs.deleteSectionConfirm', { title })}
          </DialogContentText>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {isTheme
                ? t('programs.deletion.themeIntro', { title })
                : t('programs.deletion.sectionIntro', { title })}
            </DialogContentText>

            {startedSelected > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('programs.deletion.startedWarning', { count: startedSelected })}
              </Alert>
            )}

            {selected.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('programs.deletion.nothingSelected')}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                columnGap: 2,
              }}
            >
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={submitting}
                    onChange={(event) =>
                      setSelected(event.target.checked ? items.map((item) => item.assignment_id) : [])
                    }
                  />
                }
                label={t('programs.deletion.selectAll')}
              />
              <Typography variant="body2" color="text.secondary">
                {t('programs.deletion.selectedCount', { selected: selected.length, total: items.length })}
              </Typography>
            </Box>

            <Divider />

            <List dense disablePadding aria-label={t('programs.deletion.listLabel')}>
              {items.map((item) => {
                const labelId = `${listId}-${item.assignment_id}`
                const checked = selected.includes(item.assignment_id)

                return (
                  <ListItem key={item.assignment_id} disablePadding divider>
                    <ListItemButton
                      disabled={submitting}
                      onClick={() => toggle(item.assignment_id)}
                      sx={{ gap: 1, px: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          slotProps={{ input: { 'aria-labelledby': labelId } }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                        primary={
                          item.recipient.name ?? t('programs.deletion.unnamed', { id: item.assignment_id })
                        }
                        secondary={recipientLabel(item)}
                      />
                      {item.theme_status !== null && (
                        <Chip
                          size="small"
                          variant={item.theme_status === 'planned' ? 'outlined' : 'filled'}
                          color={STATUS_COLOR[item.theme_status]}
                          label={t(`programs.status.${STATUS_KEY[item.theme_status]}`)}
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                      {item.themes_count !== null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('programs.themesCount', { count: item.themes_count })}
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={target === null || loading || loadError !== null || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => void submit()}
        >
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
